import {
    Component,
    ElementRef,
    HostListener,
    OnDestroy,
    OnInit,
    viewChild,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { AppState } from "../store/waitingroom.state";
import { LogAppointmentService } from "../../logging/providers/log-appointment.service";
import { BrandingService } from "../../branding/providers/branding.service";
import { Observable, Subscription } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";
import { MultiUserAppointmentService } from "../../appointment/providers/multi-user-appointment.service";
import {
    MultiUserAppointment,
    MultiUserAppointmentRequestType,
} from "../../appointment/store/appointment/multi-user-appointment.entity";
import { getIsOnCallSelector } from "../store/waitingroom.selector";
import { AccessibilityService } from "../../../providers/accessibility/accessibility.service";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { NgStyle, AsyncPipe } from "@angular/common";
import { MultiUserAppointmentInfoForPatientsComponent } from "../../appointment/components/appointment-info-for-patients/multi-user-appointment-info-for-patients/multi-user-appointment-info-for-patients.component";
import { WaitingroomComponent } from "../single-user-waitingroom/waitingroom.component";

@Component({
    selector: "app-multi-user-waitingroom",
    templateUrl: "./multi-user-waitingroom.component.html",
    styleUrls: ["./multi-user-waitingroom.component.scss"],
    imports: [
        NgStyle,
        MultiUserAppointmentInfoForPatientsComponent,
        AsyncPipe,
        TranslatePipe,
    ],
})
export class MultiUserWaitingroomComponent implements OnInit, OnDestroy {
    public appointments$: Observable<MultiUserAppointment[]>;
    public backgroundImg$: Observable<{ backgroundImage: string }>;

    readonly itemsRef = viewChild<ElementRef>("itemsRef");
    private isOnCall = false;
    private readonly subscriptionIsOnCall: Subscription | null = null;
    private readonly defaultBgImage =
        "/assets/img/waitingroom/waitingroom-patients.jpg";

    constructor(
        private store: Store<AppState>,
        private appointmentService: MultiUserAppointmentService,
        private tracer: LogAppointmentService,
        private brandingService: BrandingService,
        private accessibilityService: AccessibilityService,
        private translate: TranslateService,
    ) {
        this.appointments$ =
            this.appointmentService.getAllMultiUserAppointments$();

        this.subscriptionIsOnCall = this.store
            .select(getIsOnCallSelector)
            .pipe(distinctUntilChanged())
            .subscribe((onCall) => (this.isOnCall = onCall));

        this.backgroundImg$ = this.brandingService
            .getBrandingSettingsFromStore()
            .pipe(
                map((settings) => {
                    const backgroundImage =
                        settings.bgImage_patientWaitingRoom ||
                        this.defaultBgImage;
                    return { backgroundImage };
                }),
            );
    }

    @HostListener("window:beforeunload", ["$event"])
    unloadHandler(event: BeforeUnloadEvent) {
        if (this.isOnCall) {
            this.tracer.logAppointmentQuit();
        }
        event.stopPropagation();
    }

    ngOnInit() {
        // BFSG: Announce waiting room loading to screen readers
        this.translate
            .get("ACCESSIBILITY.SR_WAITING_ROOM_LOADING")
            .subscribe((message) => {
                this.accessibilityService.announceToScreenReader(
                    message,
                    "polite",
                );
            });

        this.appointmentService.getAppointments(
            MultiUserAppointmentRequestType.ALL,
        );

        // BFSG: Announce when appointments are loaded
        this.appointments$.subscribe((appointments) => {
            if (appointments && appointments.length > 0) {
                this.translate
                    .get("ACCESSIBILITY.SR_APPOINTMENTS_FOUND", {
                        count: appointments.length,
                    })
                    .subscribe((message) => {
                        this.accessibilityService.announceToScreenReader(
                            message,
                            "polite",
                        );
                    });

                // BFSG: Don't auto-focus to preserve skip-to-main-content functionality
                // Users should be able to tab to skip link first, then choose to navigate to content
            } else {
                this.translate
                    .get("ACCESSIBILITY.SR_NO_APPOINTMENTS_FOUND")
                    .subscribe((message) => {
                        this.accessibilityService.announceToScreenReader(
                            message,
                            "polite",
                        );
                    });
            }
        });
    }

    ngOnDestroy(): void {
        if (this.subscriptionIsOnCall !== null) {
            this.subscriptionIsOnCall.unsubscribe();
        }
    }
}
